import Box from "@/components/Box";
import DataLoader from "@/components/DataLoader";
import Divider from "@/components/Divider";
import { ExternalTextLink } from "@/components/Links";
import OrderStatusBadges from "@/components/OrderStatusBadges";
import Page from "@/components/Page";
import OrderedBy from "@/components/organisations/orders/shared/OrderedBy";
import OrderListItem from "@/components/organisations/orders/ui/OrderListItem";
import { getAPIBase } from "@/core/config";
import {
  BASKET_PAYEE_PATIENT,
  ORDER_PRODUCT_REORDER,
  PAYMENT_INITIATED,
  PAYMENT_OBJECT_STATUS_PAID
} from "@/core/constants";
import { getOrderUrl } from "@/core/urls";
import { ORDER_QUERY, READ_ONLY_ORDER_QUERY } from "@/graphql/shop";
import Badge from "@/tpo/Badge";
import { PanelBoxV2 } from "@/tpo/Boxes";
import Center from "@/tpo/Center";
import Currency from "@/tpo/Currency";
import Group from "@/tpo/Group";
import Jumbotron from "@/tpo/Jumbotron";
import NothingToShow from "@/tpo/NothingToShow";
import Spacer from "@/tpo/Spacer";
import Stack from "@/tpo/Stack";
import {
  PAYMENT_STATUS_AWAITING_PAYMENT,
  PAYMENT_STATUS_PAID,
  PAYMENT_STATUS_PATIENT_PAID,
  PAYMENT_STATUS_PATIENT_TO_PAY
} from "@/tpo/shop/constants";
import { useTheme } from "styled-components";

import B2CHeader from "../../tpo/header/B2CHeader";
import OrderDetailTemplate from "./OrderDetailTemplate";

function getUnpaidCheckoutPaymentLink({
  sourceBasket,
  status,
  orderId,
  patientPayCheckoutRedirectUrl
}) {
  if (!sourceBasket || status !== PAYMENT_INITIATED) {
    return null;
  }

  const isPatient = sourceBasket.payee === BASKET_PAYEE_PATIENT;

  if (isPatient) {
    return patientPayCheckoutRedirectUrl
      ? { url: patientPayCheckoutRedirectUrl, text: "Payment Link" }
      : null;
  }

  const paymentCheckoutSession = sourceBasket.paymentCheckoutSession;
  if (
    !paymentCheckoutSession ||
    paymentCheckoutSession.paymentStatus === PAYMENT_OBJECT_STATUS_PAID
  ) {
    return null;
  }

  return {
    url: `${getAPIBase()}/payments/checkout/${orderId}/`,
    text: "Pay now"
  };
}

export function OrderDetails({
  id,
  orderNo,
  orderDate,
  estimatedResultsAvailableDate,
  estimatedResultsAvailableDateHelpText,
  userFullName,
  derivedPaymentStatus,
  derivedProgressStatus,
  sourceBasket,
  status,
  showPaymentStatus,
  patientPayCheckoutRedirectUrl
}) {
  let additionalStatus = null;
  if (status === ORDER_PRODUCT_REORDER) {
    additionalStatus = status;
  }

  const paymentLink = getUnpaidCheckoutPaymentLink({
    sourceBasket,
    status,
    orderId: id,
    patientPayCheckoutRedirectUrl
  });

  return (
    <Box
      display="flex"
      flexDirection={["column", "column", "row"]}
      justifyContent={["flex-start", "flex-start", "space-between"]}
      py={4}
      gap={20}
    >
      <Stack gap={10}>
        {additionalStatus && (
          <Box alignItems="flex-start" fontFamily="gilroyBold" fontSize={[14, 14, 16]}>
            <Badge bg="black" color="white" ml="auto" size="xs">
              {additionalStatus}
            </Badge>
          </Box>
        )}
        <Group gap={10}>
          <Box fontFamily="gilroyBold" fontSize={[14, 14, 16]}>
            Order No:
          </Box>
          <Box fontFamily="gilroyMedium" fontSize={[14, 14, 16]} data-testid="orderDetails:orderNo">
            {orderNo}
          </Box>
        </Group>
        {paymentLink ? (
          <Group gap={10}>
            <Box fontFamily="gilroyBold" fontSize={[14, 14, 16]}>
              Payment link:
            </Box>
            <ExternalTextLink
              onClick={e => {
                e.clickWithinOrderDetailsComponent = true;
              }}
              href={paymentLink.url}
              data-testid="paymentLink"
            >
              {paymentLink.text}
            </ExternalTextLink>
          </Group>
        ) : null}
        <Group gap={10}>
          <Box fontFamily="gilroyBold" fontSize={[14, 14, 16]}>
            Order Date:
          </Box>
          {!!orderDate && (
            <Box
              fontFamily="gilroyMedium"
              fontSize={[14, 14, 16]}
              data-testid="orderDetails:orderDate"
            >
              {new Date(orderDate).toLocaleDateString("en-GB")}
            </Box>
          )}
        </Group>
        {!!estimatedResultsAvailableDate && (
          <Box>
            <Group gap={10}>
              <Box fontFamily="gilroyBold" fontSize={[14, 14, 16]}>
                Results expected:
              </Box>
              {!!estimatedResultsAvailableDate && (
                <Box
                  fontFamily="gilroyMedium"
                  fontSize={[14, 14, 16]}
                  data-testid="orderDetails:orderDate"
                >
                  {new Date(estimatedResultsAvailableDate).toLocaleDateString("en-GB")}
                </Box>
              )}
            </Group>
            {!!estimatedResultsAvailableDateHelpText && (
              <Box fontSize={14} fontFamily="gilroyMedium" ml={24}>
                {estimatedResultsAvailableDateHelpText}
              </Box>
            )}
          </Box>
        )}
        {!!userFullName && (
          <Group gap={10}>
            <Box fontFamily="gilroyBold" fontSize={[14, 14, 16]}>
              Name:
            </Box>
            <Box fontFamily="gilroyMedium" fontSize={[14, 14, 16]} data-testid="orderDetails:name">
              {userFullName}
            </Box>
          </Group>
        )}
      </Stack>
      <Box>
        <OrderStatusBadges
          order={{
            id,
            derivedPaymentStatus,
            derivedProgressStatus,
            sourceBasket
          }}
          showPaymentStatus={showPaymentStatus}
          derivedPaymentStatusOverrides={{
            [PAYMENT_STATUS_PATIENT_TO_PAY]: PAYMENT_STATUS_AWAITING_PAYMENT,
            [PAYMENT_STATUS_PATIENT_PAID]: PAYMENT_STATUS_PAID
          }}
        />
      </Box>
    </Box>
  );
}

export function OrderDetailsContent({
  id,
  updated,
  checkoutDate,
  estimatedResultsAvailableDateHelpText,
  derivedPaymentStatus,
  derivedProgressStatus,
  shippingFullName,
  userFullName,
  shippingAddress1,
  shippingAddress2,
  shippingTownCity,
  shippingPostalCode,
  shippingCountry,
  testItems,
  supplementItems,
  postageCosts,
  discountedTotal,
  totalDiscount,
  vat,
  refunded,
  sourceBasket,
  status,
  order,
  patientPayCheckoutRedirectUrl
}) {
  const showPrices = !sourceBasket || (sourceBasket && sourceBasket.payee === BASKET_PAYEE_PATIENT);
  // VAT is only paid on consumer orders
  const showVat = !sourceBasket;

  let fullDisplayPrice = 0;
  if (sourceBasket) {
    fullDisplayPrice = sourceBasket.basketTotalPrice;
  } else {
    fullDisplayPrice = discountedTotal + postageCosts;
  }

  let shippingDisplayPrice = 0;
  if (sourceBasket) {
    shippingDisplayPrice = sourceBasket.basketShippingCost;
  } else {
    shippingDisplayPrice = postageCosts;
  }

  let displayCurrencySymbol = sourceBasket?.currencySymbol || "£";

  let orderedByUi = null;
  if (order) {
    if (sourceBasket) {
      orderedByUi = <OrderedBy order={order} cmpContext={false} />;
    } else if (order.orderCopiedFrom) {
      orderedByUi = (
        <Box fontFamily="gilroyMedium" fontSize={18}>
          Original order:{" "}
          <ExternalTextLink href={getOrderUrl(order.orderCopiedFrom.id)}>
            {order.orderCopiedFrom.id}
          </ExternalTextLink>
        </Box>
      );
    }
  }

  return (
    <>
      {orderedByUi && (
        <Box px={20} py={20}>
          <Box maxWidth={860} mx="auto">
            {orderedByUi}
          </Box>
        </Box>
      )}
      <PanelBoxV2
        maxWidth={860}
        outer={{
          bg: "haze",
          py: 20,
          px: 20
        }}
      >
        <Box py={20}>
          <OrderDetails
            showPaymentStatus={showPrices}
            id={id}
            orderNo={id}
            orderDate={checkoutDate}
            userFullName={userFullName}
            derivedPaymentStatus={derivedPaymentStatus}
            derivedProgressStatus={derivedProgressStatus}
            sourceBasket={sourceBasket}
            status={status}
            patientPayCheckoutRedirectUrl={patientPayCheckoutRedirectUrl}
          />
        </Box>
        <Divider />
        <Stack py={20}>
          <Box fontFamily="gilroyBold" fontSize={16}>
            Shipping address
          </Box>
          <Stack fontFamily="gilroyMedium" fontSize={14}>
            {shippingFullName && (
              <span
                fontFamily="inherit"
                fontSize="inherit"
                style={{
                  wordBreak: "break-word",
                  wordWrap: "anywhere"
                }}
                data-component-name="ShippingFullName"
              >
                {shippingFullName}
              </span>
            )}
            {shippingAddress1 && (
              <span
                fontFamily="inherit"
                fontSize="inherit"
                style={{
                  wordBreak: "break-word",
                  wordWrap: "anywhere"
                }}
                data-component-name="ShippingAddress1"
              >
                {shippingAddress1}
              </span>
            )}
            {shippingAddress2 && (
              <span
                fontFamily="inherit"
                fontSize="inherit"
                style={{
                  wordBreak: "break-word",
                  wordWrap: "anywhere"
                }}
                data-component-name="ShippingAddress2"
              >
                {shippingAddress2}
              </span>
            )}
            {shippingTownCity && (
              <span
                fontFamily="inherit"
                fontSize="inherit"
                style={{
                  wordBreak: "break-word",
                  wordWrap: "anywhere"
                }}
                data-component-name="ShippingTownCity"
              >
                {shippingTownCity}
              </span>
            )}
            {shippingPostalCode && (
              <span
                fontFamily="inherit"
                fontSize="inherit"
                style={{
                  wordBreak: "break-word",
                  wordWrap: "anywhere"
                }}
                data-component-name="ShippingPostalCode"
              >
                {shippingPostalCode}
              </span>
            )}
            {shippingCountry && (
              <span
                fontFamily="inherit"
                fontSize="inherit"
                style={{
                  wordBreak: "break-word",
                  wordWrap: "anywhere"
                }}
                data-component-name="ShippingCountry"
              >
                {shippingCountry}
              </span>
            )}
          </Stack>
        </Stack>
      </PanelBoxV2>
      <PanelBoxV2
        maxWidth={860}
        outer={{
          pt: [30, 30, 60],
          pb: [60, 60, 120],
          px: 20,
          "data-component-name": "OrderDetailItems"
        }}
        stacked
        gap={20}
      >
        {testItems?.map(lineItem => {
          return (
            <OrderListItem
              consumerPerspective={true}
              order={order}
              orderTestItem={lineItem}
              name={lineItem.nameInBasket}
              registrationId={lineItem.providerTestId}
              price={lineItem.price}
              clinicLocation={lineItem.clinicLocation}
              clinicBooking={lineItem.clinicBooking}
              key={lineItem.id}
              isAddOn={!!lineItem.parent}
              currencySymbol={displayCurrencySymbol}
              supppressPrice={!showPrices}
              registeredUserFullName={lineItem.registeredUserFullName}
            />
          );
        })}
        {supplementItems?.map(lineItem => (
          <OrderListItem
            order={order}
            orderTestItem={lineItem}
            name={lineItem.nameInBasket}
            price={lineItem.price}
            key={lineItem.id}
            currencySymbol={displayCurrencySymbol}
            supppressPrice={!showPrices}
            consumerPerspective={true}
            showBadges={false}
          />
        ))}
        <Spacer py={20} />
        {showPrices ? (
          <Stack gap={10} alignItems="flex-end">
            {!!totalDiscount && (
              <Currency
                symbol={displayCurrencySymbol}
                prefix="DISCOUNT"
                value={-1 * totalDiscount}
                fontSize={28}
                data-testid="discountTotal"
              />
            )}
            <Currency
              symbol={displayCurrencySymbol}
              prefix="SHIPPING"
              value={shippingDisplayPrice}
              fontSize={28}
              data-testid="shippingTotal"
            />
            <Currency
              symbol={displayCurrencySymbol}
              prefix="VAT"
              value={showVat ? vat : 0}
              fontSize={28}
              data-testid="vatTotal"
            />
            <Currency
              symbol={displayCurrencySymbol}
              prefix="TOTAL"
              value={fullDisplayPrice.toFixed(2)}
              fontSize={[28, 28, 44]}
              data-testid="orderTotal"
            />
            {!!refunded && (
              <Currency
                symbol={displayCurrencySymbol}
                prefix="REFUNDED"
                value={refunded}
                fontSize={28}
                data-testid="refundTotal"
                color="red"
              />
            )}
          </Stack>
        ) : (
          <Stack alignItems="flex-end" gap={0} data-testid="orderPlacedBy">
            <Box fontFamily="gilroyBold" fontSize={24} textTransform="uppercase">
              order placed by:
            </Box>
            <Box
              fontFamily="gilroyBold"
              fontSize={24}
              textTransform="uppercase"
              data-testid="orderPlacedBy:name"
            >
              {order.orderingOrganisationName ||
                order.practitionerWhoPlacedOrderFullName}
            </Box>
          </Stack>
        )}
      </PanelBoxV2>
    </>
  );
}

export function OrderNotFound({ tabs, partners = false }) {
  const theme = useTheme();

  return (
    <Page
      bg="haze"
      header={
        <Jumbotron
          title="Order"
          bg={partners ? "partners" : "purple"}
          color={partners ? "dark" : "white"}
        />
      }
    >
      {tabs}
      <Center pt={theme.spacing.section.pt} pb={theme.spacing.section.pb} px={20}>
        <NothingToShow header="Not found">
          <Box fontFamily="gilroyMedium" fontSize={[14, 14, 16]}>
            I'm sorry, we can't seem to find that order. Please contact{" "}
            <ExternalTextLink href="mailto:info@regeneruslabs.com">
              info@regeneruslabs.com
            </ExternalTextLink>
          </Box>
        </NothingToShow>
      </Center>
    </Page>
  );
}

export function Order({ order }) {
  return (
    <OrderDetailTemplate title="Order Detail" gap={0}>
      <OrderDetailsContent {...order} order={order} />
    </OrderDetailTemplate>
  );
}

export function ReadOnlyOrderDetailPage({
  match: {
    params: { id }
  }
}) {
  return (
    <>
      <B2CHeader />
      <DataLoader
        query={READ_ONLY_ORDER_QUERY}
        variables={{
          id
        }}
        render={({ order }) => {
          if (order?.sourceBasket) {
            return (
              <OrderDetailTemplate title="Order Detail" gap={0} partners={true}>
                <OrderDetailsContent {...order} order={order} />
              </OrderDetailTemplate>
            );
          }
          return <OrderNotFound partners />;
        }}
      />
    </>
  );
}

export default function OrderDetailPage({
  match: {
    params: { id }
  }
}) {
  return (
    <>
      <B2CHeader />
      <DataLoader
        query={ORDER_QUERY}
        variables={{
          id
        }}
        render={({ order }) => (order ? <Order order={order} /> : <OrderNotFound />)}
      />
    </>
  );
}
